import { useState } from "react";
import { AppWindow, ExternalLink, Video } from "lucide-react";
import { delegateApi, denyMessage } from "../../lib/delegate";
import { SWITCH_MSG, goToEventRoom, openEventWindow, useZoomMeeting } from "../../lib/meeting";
import { useTheatre } from "../site/SiteLayout";
import { StageControls } from "./StageControls";

function BackupLink() {
  const [state, setState] = useState(null);
  const fetchLink = async () => {
    try { const { data } = await delegateApi.get("/virtual/backup-link"); setState(data); }
    catch (e) { setState({ available: false, message: denyMessage(e) }); }
  };
  if (!state) return <button type="button" className="btn-outline" onClick={fetchLink} data-testid="backup-link-btn"><ExternalLink size={18} aria-hidden="true" /> Join in Zoom (backup)</button>;
  if (!state.available) return <p className="text-sm text-[#4A5568]" role="status" data-testid="backup-link-unavailable">{state.message}</p>;
  return <a href={state.join_url} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="backup-link-open">Open personal Zoom link</a>;
}

export default function ZoomJoin({ me }) {
  const { theatre, setTheatre } = useTheatre();
  const m = useZoomMeeting(theatre);
  const leave = async (msg) => { await m.leave(msg); setTheatre(false); };
  const openWindow = () => {
    if (m.active && !window.confirm(SWITCH_MSG)) return;
    if (!openEventWindow(m.active ? () => leave() : undefined)) goToEventRoom();
  };
  const canJoin = me.embedded_enabled && me.zoom_configured;

  return (
    <div data-testid="zoom-join">
      {!me.zoom_configured && <p className="mb-4 rounded bg-paleblue p-4 font-semibold text-navy" role="status" data-testid="zoom-not-configured">Zoom not yet configured. Please check back closer to the event.</p>}
      {m.state.phase === "left" && <p className="mb-4 text-sm text-[#4A5568]" role="status" data-testid="zoom-left">{m.state.message || "You have left the session."} You can rejoin while your verified session is active.</p>}
      <div className="flex flex-wrap gap-4">
        {canJoin && <button type="button" className="btn-primary" onClick={openWindow} data-testid="open-event-window-btn"><AppWindow size={18} aria-hidden="true" /> Open event window</button>}
        {canJoin && !m.active && (
          <button type="button" className="btn-outline" onClick={m.join} disabled={m.joining} data-testid="zoom-join-embedded-btn">
            <Video size={18} aria-hidden="true" /> {m.state.phase === "left" ? "Rejoin here" : m.state.phase === "error" ? "Try again" : "Join here"}</button>
        )}
        {me.backup_available && <BackupLink />}
      </div>
      {m.state.phase === "error" && <p role="alert" className="mt-4 text-burgundy" data-testid="zoom-join-error">{m.state.message}
        {m.state.details && <small className="block text-xs text-[#4A5568]" data-testid="zoom-join-error-details">{m.state.details}</small>}</p>}
      {/* Full-bleed stage (~95vw), centred under the page column. The Zoom root stays mounted in every phase. */}
      <div ref={m.stage} className={`relative ml-[calc(50%_-_47.5vw)] mt-6 w-[95vw] overflow-hidden rounded-lg ${m.active ? "bg-[#0b1a2e]" : ""}`} data-testid="meeting-stage">
        {m.active && <StageControls phase={m.state.phase} onLeave={leave} />}
        <div ref={m.root} id="meetingSDKElement" style={m.rootStyle} data-testid="zoom-embed-root" />
      </div>
    </div>
  );
}
